import { GlobalContext } from 'context';
import { UserDetails, getDecryptedValue, Proxy } from 'common';
import {
  startPreLoginIdleMonitoring,
  stopPreLoginIdleMonitoring,
} from 'common/utils/preLoginIdleMonitor';

import React, {
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from 'react';

type AuthProviderProps = {
  children: React.ReactNode;
  token?: string | null;
};

interface AuthContextType {
  user: string | null;
  login: (user: string | null) => void;
  logout: () => void;
}

export const HOCAuthToken = <P extends object>(
  AuthTokenWrapper: React.ComponentType<P>
) => {
  return function SetAuthComponent(props: AuthProviderProps) {
    const [token, setToken] = useState<string>('');
    const [isCallMade, setIsCallMade] = useState(false);
    const {
      setAuthToken,
      setProxyMemberId,
      setXauthToken,
      setXsentaraTokenID,
    } = useContext(GlobalContext);

    useEffect(() => {
      const callApi = async () => {
        const authToken = await getDecryptedValue(UserDetails.authToken);
        const proxyId = await getDecryptedValue(Proxy.MemberId);
        setToken(authToken);
        setAuthToken(authToken);
        setProxyMemberId(proxyId);

        // No personId means the user is not logged in yet the pre-login
        // idle monitor so anonymous OpenReplay sessions stop after inactivity.
        const personId = await getDecryptedValue(UserDetails.personId);
        if (personId) {
          stopPreLoginIdleMonitoring();
        } else {
          startPreLoginIdleMonitoring();
        }
        const personalInfo = await getDecryptedValue(UserDetails.guest);
        if (personalInfo) {
          const parsed = JSON.parse(personalInfo);
          if (parsed?.xAuthToken) setXauthToken(parsed.xAuthToken);
          if (parsed?.xSentaraTokenID)
            setXsentaraTokenID(parsed.xSentaraTokenID);
        }

        setIsCallMade(true);
      };
      callApi();

      return () => {
        stopPreLoginIdleMonitoring();
      };
    }, []);
    return isCallMade ? (
      <AuthTokenWrapper {...{ ...(props as P), token }} />
    ) : null;
  };
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = HOCAuthToken(
  ({ children, token }: AuthProviderProps) => {
    const [user, setUser] = useState<string | null>(token ?? null);

    const login = useCallback((user: string | null) => {
      stopPreLoginIdleMonitoring();
      setUser(user);
    }, []);

    const logout = useCallback(() => {
      stopPreLoginIdleMonitoring();
      setUser(null);
    }, []);

    const authContextValue = useMemo(
      () => ({
        user,
        login,
        logout,
      }),
      [user, login, logout]
    );

    return (
      <AuthContext.Provider value={authContextValue}>
        {children}
      </AuthContext.Provider>
    );
  }
);

export const useAuth = () => {
  return useContext(AuthContext);
};
